import { useState } from 'react';
import { useReduxState } from 'hooks';
import { useApiData } from 'lib/api';
import { getMergedCompetencies } from 'lib/competencies';
import { LoadingIndicator, Tooltip } from 'components/common';
import { Modal, ModalContent, ModalPortal } from 'components/common/modal';
import { ProfileCompetenciesCompareResult } from 'components/profile';
import { CompetencyMatrixMatchStatus } from '.';

import type { GetUsersForCompetencyMatrixRes } from 'shared-types/responses';
import type { CompetencyInMatrix } from 'lib/competencies/getCompetencyMatrixColumnsByView';

interface Props {
  profile: CompetencyInMatrix;
  user: GetUsersForCompetencyMatrixRes['users'][0];
}

const MatchResults = (props: Props & { metReqs: number }) => {
  const { data, loading } = useApiData({
    url: `/admin/users/${props.user.id}/competencies`
  });

  const mergedCompetencies = loading
    ? []
    : getMergedCompetencies(data?.userCompetencies || []);

  return loading ? (
    <LoadingIndicator />
  ) : (
    <ProfileCompetenciesCompareResult
      profileCompetencies={props.profile.competencies}
      numberOfMatchedCompetencies={props.metReqs}
      numberOfTotalCompetencies={props.profile.competencies.length}
      user={props.user}
      userCompetencies={mergedCompetencies}
    />
  );
};

const CompetencyMatrixUsersListProfileMatch = ({ profile, user }: Props) => {
  const { messages } = useReduxState();

  const [modalVisible, setModalVisible] = useState(false);

  const numberOfMetRequirements = profile.competencies?.reduce(
    (count, competency) => {
      const userCompetency = Number(user.competencies?.[competency.id]);
      const requiredLevel = competency.level?.level || 0;
      if (
        (requiredLevel && userCompetency && userCompetency >= requiredLevel) ||
        (!requiredLevel && userCompetency)
      ) {
        return count + 1;
      }
      return count;
    },
    0
  );

  const progress = numberOfMetRequirements / profile.competencies?.length;

  return (
    <Tooltip content={messages.commonShowDetails}>
      <div onClick={() => setModalVisible(true)} style={{ cursor: 'pointer' }}>
        <CompetencyMatrixMatchStatus progress={progress} />

        <ModalPortal>
          <Modal
            closeButton={true}
            onHide={() => setModalVisible(false)}
            visible={modalVisible}
            title={`${messages.competencyProfile}: ${profile.title}`}
            width="medium"
          >
            <ModalContent>
              {modalVisible ? (
                <MatchResults
                  metReqs={numberOfMetRequirements}
                  profile={profile}
                  user={user}
                />
              ) : null}
            </ModalContent>
          </Modal>
        </ModalPortal>
      </div>
    </Tooltip>
  );
};

export default CompetencyMatrixUsersListProfileMatch;
